import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, QueryList, signal, ViewChildren} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {HttpErrorResponse} from "@angular/common/http";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {NgbDateStruct, NgbInputDatepicker} from "@ng-bootstrap/ng-bootstrap";
import {saveAs} from "file-saver";
import {Subscription} from "rxjs";
import {formatDate} from "@angular/common";
import {ApiService} from "@app/_services/api.service";
import {ToastService} from "@app/_services/toast.service";
import {Customer, LogEntryItem, LogEntryUser} from "@app/_models/api";
import {toDateStruct, toIsoDate} from "@app/_helpers/date-struct";
import {isControlInvalid} from "@app/_helpers/form-validation";
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";
import {
    AuditLogFilters,
    AuditLogSortColumn,
    buildCsv,
    defaultFilters,
    formatEasternDateTime,
    recordText,
    sortItems,
    toSearchParams,
    totalText,
    truncationNote,
    VINEMEDS_ADMIN_CUSTOMER_ID
} from "@app/audit-log/audit-log.helpers";

type AuditLogData = { customers: Customer[]; tables: string[]; users: LogEntryUser[]; failedLists: string[] };

@Component({
    selector: "app-audit-log",
    imports: [ReactiveFormsModule, NgbInputDatepicker, SortableDirective, TablePagerComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./audit-log.component.html",
    styleUrl: "./audit-log.component.scss"
})
export class AuditLogComponent {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;

    readonly vineMedsAdminCustomerId = VINEMEDS_ADMIN_CUSTOMER_ID;
    readonly customers: Customer[];
    readonly tables: string[];
    readonly users: LogEntryUser[];
    readonly failedLists: string[];

    readonly form = new FormGroup({
        customerId: new FormControl("", {nonNullable: true}),
        cognitoUserId: new FormControl("", {nonNullable: true}),
        tableName: new FormControl("", {nonNullable: true}),
        from: new FormControl<NgbDateStruct | null>(null),
        to: new FormControl<NgbDateStruct | null>(null),
        value: new FormControl("", {nonNullable: true}),
        contains: new FormControl(false, {nonNullable: true})
    });

    readonly items = signal<LogEntryItem[]>([]);
    readonly totalCount = signal(0);
    readonly note = signal<string | null>(null);
    readonly page = signal(1);
    readonly pageSize = signal(100);
    readonly loading = signal(false);
    readonly loadFailed = signal(false);
    readonly errorMessage = signal<string | null>(null);
    readonly sort = signal<SortEvent>({column: "", direction: ""});

    readonly sortedItems = computed(() =>
        sortItems(this.items(), this.sort().column as AuditLogSortColumn | "", this.sort().direction));

    readonly pageItems = computed(() => {
        const start = (this.page() - 1) * this.pageSize();
        return this.sortedItems().slice(start, start + this.pageSize());
    });

    protected readonly formatEasternDateTime = formatEasternDateTime;
    protected readonly recordText = recordText;
    protected readonly totalText = totalText;
    protected readonly isControlInvalid = isControlInvalid;

    private readonly route = inject(ActivatedRoute);
    private readonly apiService = inject(ApiService);
    private readonly toastService = inject(ToastService);
    private searchSubscription?: Subscription;

    constructor() {
        const data = this.route.snapshot.data["resolvedData"] as AuditLogData;
        this.customers = [...data.customers].sort((a, b) => a.name.localeCompare(b.name));
        this.tables = data.tables;
        this.users = data.users;
        this.failedLists = data.failedLists;

        inject(DestroyRef).onDestroy(() => this.searchSubscription?.unsubscribe());

        this.applyFilters(defaultFilters());
        this.search();
    }

    search(): void {
        if (this.form.controls.from.invalid || this.form.controls.to.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.searchSubscription?.unsubscribe();
        this.items.set([]);
        this.totalCount.set(0);
        this.note.set(null);
        this.page.set(1);
        this.sort.set({column: "", direction: ""});
        this.headers?.forEach(header => header.direction = "");
        this.loadFailed.set(false);
        this.errorMessage.set(null);
        this.loading.set(true);

        this.searchSubscription = this.apiService.searchLogEntries(toSearchParams(this.readFilters())).subscribe({
            next: (response) => {
                this.loading.set(false);
                this.items.set(response.items);
                this.totalCount.set(response.totalCount);
                this.note.set(truncationNote(response));
            },
            error: (error: HttpErrorResponse) => {
                this.loading.set(false);
                if (error.status === 400) {
                    this.errorMessage.set(error.error?.detail ?? "The search filters are not valid.");
                    return;
                }

                this.loadFailed.set(true);
                this.toastService.showDanger("Search failed", "Failed to load the audit log.");
            }
        });
    }

    onSort(event: SortEvent): void {
        for (const header of this.headers) {
            if (header.sortable !== event.column) {
                header.direction = "";
            }
        }

        this.sort.set(event);
        this.page.set(1);
    }

    reset(): void {
        this.applyFilters(defaultFilters());
        this.search();
    }

    exportCsv(): void {
        const items = this.sortedItems();

        if (items.length === 0) {
            this.toastService.showInfo("Export CSV", "There are no changes matching the current filters to export.");
            return;
        }

        const timestamp = formatDate(new Date(), "yyyyMMdd-HHmm", "en-US");
        // Leading BOM so Excel on Windows opens the file as UTF-8 instead of the system codepage.
        saveAs(new Blob(["﻿" + buildCsv(items)], {type: "text/csv;charset=utf-8;"}),
            `audit-log-${timestamp}.csv`);
    }

    private readFilters(): AuditLogFilters {
        const value = this.form.getRawValue();

        return {
            ...value,
            from: toIsoDate(value.from),
            to: toIsoDate(value.to)
        };
    }

    private applyFilters(filters: AuditLogFilters): void {
        this.form.setValue({
            customerId: filters.customerId,
            cognitoUserId: filters.cognitoUserId,
            tableName: filters.tableName,
            from: toDateStruct(filters.from),
            to: toDateStruct(filters.to),
            value: filters.value,
            contains: filters.contains
        });
    }
}
